import { GetOperation, PostOperation } from '@aws-amplify/api-rest/src/types'
import { logger } from 'app/logging/Logger'
import { AppError, ErrorCategory, ErrorSeverity } from 'app/errors/types'

/**
 * API Requester
 *
 * Handles AWS Amplify API operations with proper error handling and type safety.
 * Provides typed request methods for GET and POST operations.
 *
 * Requirements: 9.5
 */
export class ApiRequester {
    /**
     * Execute a GET operation and return typed response
     *
     * @param operation - AWS Amplify GET operation
     * @returns Parsed JSON response
     * @throws AppError if request fails
     */
    static async callGetOperationRequest<T = unknown>(
        operation: GetOperation
    ): Promise<T> {
        try {
            const { body } = await operation.response
            const data = await body.json()

            logger.debug('GET request successful', {
                action: 'api_get_request',
                metadata: { hasData: data !== null },
            })

            return data as T
        } catch (error) {
            logger.error('GET request failed', error as Error, {
                action: 'api_get_request_error',
                metadata: {
                    error:
                        error instanceof Error ? error.message : String(error),
                },
            })

            throw new AppError('API GET request failed', {
                code: 'API_GET_ERROR',
                severity: ErrorSeverity.HIGH,
                category: ErrorCategory.NETWORK,
                recoverable: true,
                userMessage: 'Failed to fetch data. Please try again.',
                originalError:
                    error instanceof Error ? error : new Error(String(error)),
                context: {
                    action: 'api_get_request',
                    metadata: { error: String(error) },
                },
            })
        }
    }

    /**
     * Execute a POST operation and return typed response
     *
     * @param operation - AWS Amplify POST operation
     * @returns Parsed JSON response
     * @throws AppError if request fails
     */
    static async callPostOperationRequest<T = unknown>(
        operation: PostOperation
    ): Promise<T> {
        try {
            const { body } = await operation.response
            const data = await body.json()

            logger.debug('POST request successful', {
                action: 'api_post_request',
                metadata: { hasData: data !== null },
            })

            return data as T
        } catch (error) {
            logger.error('POST request failed', error as Error, {
                action: 'api_post_request_error',
                metadata: {
                    error:
                        error instanceof Error ? error.message : String(error),
                },
            })

            throw new AppError('API POST request failed', {
                code: 'API_POST_ERROR',
                severity: ErrorSeverity.HIGH,
                category: ErrorCategory.NETWORK,
                recoverable: true,
                userMessage: 'Failed to submit data. Please try again.',
                originalError:
                    error instanceof Error ? error : new Error(String(error)),
                context: {
                    action: 'api_post_request',
                    metadata: { error: String(error) },
                },
            })
        }
    }
}
